import { AGENT_BY_ID } from "../agents";
import { checkState, lerpSeries } from "../story";

const Row = ({ k, v, tone, testid }) => (
  <div className="flex items-baseline justify-between gap-3 py-1 border-b border-white/[0.06] last:border-0" data-testid={testid}>
    <span className="text-white/45">{k}</span><span className={`text-right ${tone || "text-white/85"}`}>{v}</span>
  </div>
);
const Head = ({ children, color = "#a78bfa" }) => <div className="cine-mono text-[10px] tracking-[0.3em] mb-2" style={{ color }}>{children}</div>;
const STREAM = ["MOTHWING", "LUMENRAT", "OBSIDIAN", "GLITCHFOX", "NEBULAPUG", "QUARTZOWL", "VOLTKOI", "PIXELYAK"];

function Discovery({ t, u, beatId, sc }) {
  const shift = Math.floor(t * 2);
  return (
    <div data-testid="panel-discovery">
      <Head color="#22D3EE">INCOMING · ILLUSTRATIVE</Head>
      <div className="grid grid-cols-2 gap-3 text-[11px] cine-mono">
        <div><div className="text-[#22D3EE]/80 mb-1">Pump.fun launches</div>{[0, 1, 2, 3].map((i) => <div key={i} className="text-white/60 truncate">${STREAM[(shift + i) % 8]} <span className="text-white/30">SIM</span></div>)}</div>
        <div><div className="text-white/70 mb-1">X via GrokBot</div>{[0, 1, 2, 3].map((i) => <div key={i} className="text-white/50 truncate">“${STREAM[(shift + i + 3) % 8]} volume waking”</div>)}</div>
      </div>
      {sc.fast && <div className="mt-3 rounded-lg border border-amber-300/30 bg-amber-300/[0.06] px-3 py-2 text-[11px] text-amber-100/90" data-testid="fast-mover-rule"><span className="cine-mono text-amber-300">⚡ FAST-MOVER FEED</span> · $1M market cap inside the first hour → priority entry · sold 60 s later</div>}
      {beatId === "focus" && <div className="mt-3 text-[11px] text-white/60">Dismissed so far: <span className="text-rose-300">{Math.round(u * 13)}</span> · converging signals on one candidate</div>}
    </div>
  );
}

function Identity({ sc }) {
  return (
    <div data-testid="panel-candidate">
      <Head>CANDIDATE · SYNTHETIC TOKEN</Head>
      <div className="text-2xl font-black cine-display">${sc.token.sym}</div>
      <div className="mt-2 text-[12px]">
        <Row k="Mint" v={sc.token.mint} /><Row k="First seen" v={`${sc.token.firstSeen} (sim clock)`} />
        <Row k="Signal source" v="Pump.fun launch stream + X mentions" />
        <Row k="Chain identity" v="✓ verified" tone="text-emerald-300" />
        <Row k="Creator history" v="? unresolved" tone="text-amber-300" />
        <Row k="Social origin" v="? pending GrokBot" tone="text-amber-300" />
      </div>
    </div>
  );
}

function Hive({ sc, u }) {
  const shown = Math.ceil(u * 6.5);
  return (
    <div data-testid="panel-hive">
      <Head>HIVE FINDINGS</Head>
      <div className="space-y-1.5">
        {sc.findings.slice(0, shown).map((f) => (
          <div key={f.agent} className="cine-in flex gap-2 text-[12px]" data-testid={`finding-${f.agent}`}>
            <span className="cine-mono shrink-0 w-[68px]" style={{ color: AGENT_BY_ID[f.agent].color }}>{AGENT_BY_ID[f.agent].name}</span>
            <span className={f.warn ? "text-amber-200" : "text-white/75"}>{f.text}</span>
          </div>
        ))}
      </div>
      {u > 0.75 && <div className="cine-in mt-3 rounded-lg border border-violet-400/30 bg-violet-500/10 px-3 py-2 text-[12px]" data-testid="oracle-verdict"><span className="text-violet-300 cine-mono">ORACLE</span> · {sc.oracle}</div>}
      <div className="mt-2 text-[10px] text-white/35">Agents agreeing is evidence, not a forecast of profit.</div>
    </div>
  );
}

function Verify({ sc, u, dur }) {
  const st = checkState(sc, u, dur);
  return (
    <div data-testid="panel-verify">
      <Head color="#34D399">SEQUENTIAL CHECKS</Head>
      <div className="space-y-1.5">
        {sc.checks.map((c, k) => {
          const state = k < st.idx ? "pass" : k === st.idx ? st.phase : "pending";
          const show = state === "pass" || state === "fail";
          return (
            <div key={c.id} className={`cine-check is-${state}`} data-testid={`check-${c.id}`} data-state={state}>
              <div className="flex items-center gap-2 text-[12px]"><span className="cine-dot" />{k + 1}. {c.label}<span className="ml-auto cine-mono text-[10px]">{state.toUpperCase()}</span></div>
              {show && <div className="pl-5 text-[11px] text-white/55">{c.detail}</div>}
            </div>
          );
        })}
      </div>
      {st.phase === "fail" && <div className="cine-in mt-3 rounded-lg border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200" data-testid="purchase-blocked">PURCHASE BLOCKED · returning to discovery</div>}
      <div className="mt-2 text-[10px] text-white/35">Passing every check does not make a token safe.</div>
    </div>
  );
}

export const ORDER_STEPS = ["ORDER PREPARED", "SUBMITTED", "CONFIRMED", "POSITION OPEN"];
export const EXIT_STEPS = ["EXIT PREPARED", "SUBMITTED", "CONFIRMED", "RECORDED"];
const stepOf = (u, cuts) => cuts.filter((c) => u >= c).length - 1;

function Steps({ steps, idx, color }) {
  return (
    <div className="grid grid-cols-4 gap-1 my-3">
      {steps.map((s, i) => (
        <div key={s} className={`cine-step ${i <= idx ? "is-on" : ""} ${i === idx ? "is-now" : ""}`} style={{ "--c": color }} data-testid={`step-${s.toLowerCase().replace(/ /g, "-")}`}>{s}</div>
      ))}
    </div>
  );
}

function Order({ sc, u }) {
  const idx = stepOf(u, [0, 0.25, 0.55, 0.8]);
  return (
    <div data-testid="panel-purchase">
      <Head color="#FB923C">SIMULATED ORDER · NOT A REAL TRANSACTION</Head>
      <Steps steps={ORDER_STEPS} idx={idx} color="#FB923C" />
      <div className="text-[12px]">
        <Row k="Side" v={`BUY $${sc.token.sym}`} /><Row k="Size" v="0.25 SIM-SOL" /><Row k="Max slippage" v="1.5%" />
        <Row k="Route" v="Aggregator route (simulated)" /><Row k="Reference" v="SIM-ORD-0418" />
        <Row k="Fill" v={idx >= 2 ? "confirmed (simulated)" : "pending"} tone={idx >= 2 ? "text-orange-300" : "text-white/50"} testid="order-fill" />
      </div>
    </div>
  );
}

function Spark({ pts, u, color }) {
  const xs = [];
  for (let i = 0; i <= 40; i++) { const uu = (i / 40) * u; xs.push([i / 40 * 100 * u, 50 - lerpSeries(pts, uu) * 0.6]); }
  return (
    <svg viewBox="0 0 100 70" className="w-full h-20" preserveAspectRatio="none" data-testid="monitor-chart">
      <line x1="0" x2="100" y1="50" y2="50" stroke="rgba(255,255,255,.15)" strokeDasharray="2 2" />
      <polyline points={xs.map((p) => p.join(",")).join(" ")} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function FastTimer({ sc, u }) {
  const left = Math.max(0, Math.ceil(sc.timer * (1 - Math.min(1, u / sc.exitAt))));
  return (
    <div className="my-2 flex items-center gap-3" data-testid="monitor-timer">
      <div className="cine-mono text-2xl font-black text-amber-300 w-14">{left}s</div>
      <div className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-amber-300 transition-[width] duration-200" style={{ width: `${(left / sc.timer) * 100}%` }} /></div>
      <div className="text-[10px] text-white/45">exit timer</div>
    </div>
  );
}

function Monitor({ sc, u }) {
  const px = lerpSeries(sc.price, u), liq = lerpSeries(sc.liq, u), color = px >= 0 ? "#34D399" : "#F43F5E";
  const ev = sc.events.filter((e) => u >= e.u);
  const exited = u >= sc.exitAt;
  return (
    <div data-testid="panel-monitor">
      <Head color="#34D399">POSITION MONITOR · EXAMPLE PATH</Head>
      <div className="flex items-end gap-4"><div className="text-3xl font-black cine-display" style={{ color }} data-testid="monitor-pnl">{px >= 0 ? "+" : ""}{px.toFixed(1)}%</div><div className="text-[11px] text-white/45 pb-1">vs simulated entry</div></div>
      <Spark pts={sc.price} u={u} color={color} />
      {sc.timer && <FastTimer sc={sc} u={u} />}
      <div className="text-[12px]">
        <Row k="Pool liquidity" v={`$${liq.toFixed(0)}k`} />
        <Row k="Holding" v={sc.fast ? (exited ? "0%" : "100%") : sc.id === "runner" && u >= 0.28 ? (exited ? "0%" : "50% (runner)") : exited ? "0%" : "100%"} />
        <Row k="Exit rule" v={sc.fast ? "Timed exit: sell 100% at 60 s" : sc.id === "runner" ? (u >= 0.45 ? "Trailing stop 10% below high" : "TP1 at +22%, hard stop −10%") : "Hard stop −10% · liquidity guard"} />
      </div>
      <div className="mt-2 space-y-1">{ev.map((e) => <div key={e.u} className={`cine-in text-[11px] cine-ev is-${e.kind}`}>{e.text}</div>)}</div>
    </div>
  );
}

function Exit({ sc, u }) {
  const idx = stepOf(u, [0, 0.3, 0.6, 0.85]);
  return (
    <div data-testid="panel-exit">
      <Head color="#FDBA74">EXIT CHANNEL · SIMULATED</Head>
      <Steps steps={EXIT_STEPS} idx={idx} color="#FDBA74" />
      <div className="text-[12px]"><Row k="Exit" v={sc.exitLabel} /><Row k="Outcome" v={`${sc.result.pct} (example)`} tone={sc.result.kind === "gain" ? "text-emerald-300" : "text-rose-300"} testid="exit-outcome" /></div>
    </div>
  );
}

function Repeat({ sc }) {
  return (
    <div data-testid="panel-repeat">
      <Head>LOOP</Head>
      <div className="text-[12px] text-white/75">{sc.failAt != null ? "No capital left the reserve. The Hive returns to discovery." : "Released capital returns to the execution reserve. Agents disperse."}</div>
      <div className="mt-2 text-[12px]"><Row k="Journal" v={`${sc.result.pct}: ${sc.result.text}`} tone="text-white/70" /></div>
    </div>
  );
}

export function StagePanel({ tl }) {
  const { beat, u, sc, t } = tl;
  const map = {
    scan: <Discovery t={t} u={u} sc={sc} beatId="scan" />, focus: <Discovery t={t} u={u} sc={sc} beatId="focus" />, candidate: <Identity sc={sc} />,
    hive: <Hive sc={sc} u={u} />, verify: <Verify sc={sc} u={u} dur={beat.dur} />, purchase: <Order sc={sc} u={u} />,
    monitor: <Monitor sc={sc} u={u} />, exit: <Exit sc={sc} u={u} />, repeat: <Repeat sc={sc} />,
  };
  return <div className="cine-panel" data-testid="cinema-stage-panel" key={`${sc.id}-${beat.id}`}>{map[beat.id]}</div>;
}
